import { useState, useEffect, useCallback, useRef } from 'react'
import { eventService } from '../services/eventService'
import type { Event } from '../types/index'
import { ApiError } from '@shoots/auth'

interface UseEventState {
  event: Event | null
  loading: boolean
  error: string | null
  isAttending: boolean | null
  rsvpLoading: boolean
  rsvpError: string | null
}

interface UseEventActions {
  fetchEvent: (eventId: string) => Promise<void>
  refreshEvent: () => Promise<void>
}

interface UseEventOptions {
  eventId?: string
  authenticated?: boolean
}

export function useEvent(
  options?: UseEventOptions | string
): UseEventState & UseEventActions {
  // Handle backward compatibility - if string is passed, treat as eventId
  const eventId = typeof options === 'string' ? options : options?.eventId
  const authenticated =
    typeof options === 'object' ? options?.authenticated : undefined
  const prevAuthenticatedRef = useRef<boolean | undefined>(authenticated)

  const [state, setState] = useState<UseEventState>({
    event: null,
    loading: false,
    error: null,
    isAttending: null,
    rsvpLoading: false,
    rsvpError: null,
  })

  const fetchEvent = useCallback(async (id: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const event = await eventService.getEvent(id)
      setState(prev => ({
        ...prev,
        event,
        loading: false,
      }))
    } catch (error) {
      const errorMessage =
        error instanceof ApiError ? error.message : 'Failed to fetch event'

      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }))
    }
  }, [])

  const refreshEvent = useCallback(async () => {
    if (state.event?.id) {
      await fetchEvent(state.event.id)
    }
  }, [state.event?.id, fetchEvent])

  // Auto-fetch event when eventId is provided
  useEffect(() => {
    if (eventId) {
      fetchEvent(eventId)
    }
  }, [eventId, fetchEvent])

  // Refetch event when authentication state changes
  useEffect(() => {
    if (
      eventId &&
      state.event &&
      authenticated !== undefined &&
      prevAuthenticatedRef.current !== authenticated
    ) {
      // Only refetch if authentication state actually changed
      fetchEvent(eventId)
    }
    // Update the ref to track the current authentication state
    prevAuthenticatedRef.current = authenticated
  }, [authenticated, eventId, fetchEvent, state.event])

  return {
    ...state,
    fetchEvent,
    refreshEvent,
  }
}
